import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, NavController } from '@ionic/angular';
import { getRegistry } from '@ngneat/elf';
import {
  ApiAuthenticationService,
  ApiPaymentsService,
  AppError,
  AuthTokenDto,
  CheckEmailForAuthenticationResult,
  LoginWithJsonWebTokenRequest,
  LoginWithJsonWebTokenResult,
  LoginWithPasswordRequest,
  LoginWithPasswordResult,
  LoginWithSamlRequest,
  LoginWithSamlResult,
  LoginWithSsoRequest,
  LoginWithSsoResult,
  RefreshTokenResult,
  ResetPasswordRequest,
  UpdateAccountInfosResult,
  UpdateEmailRequest,
} from '@timechef/core/api';
import { ReusePendingObservable } from '@timechef/core/decorators/reusePendingObservable.decorator';
import { ILogger, LoggerService } from '@timechef/core/logging';
import { AccountRepository, AccountService } from '@timechef/features/account/state';
import { RestaurantsRepository } from '@timechef/features/restaurants/state';
import { Observable, catchError, map, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppInsightsService, GoogleAnalyticsService } from '../analytics';
import { ChangePasswordRequest } from '../api/models/change-password-request';
import { AppErrorKeys, appErrorFromKey } from '../errors/error';
import { I18nService } from '../i18n/i18n.service';
import { InAppBrowserService } from '../services/in-app-browser.service';
import { PushNotificationService } from '../services/push-notification.service';
import { ensureDate, getDateExpirationStatusWithOffset } from '../utils';
import { checkRedirectUrl } from '../utils/router-utils';
import { User } from './auth.models';
import { AuthRepository, AuthStatusKeys } from './auth.repository';
import { map_UserInfoDto_User } from './auth.utils';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private logger: ILogger;
  private readonly ssoCallbackPath = '/login-sso?';

  appInsightsService = inject(AppInsightsService);

  constructor(
    private accountRepo: AccountRepository,
    private accountService: AccountService,
    private alertCtrl: AlertController,
    private authRepository: AuthRepository,
    private apiPayments: ApiPaymentsService,
    private apiAuthentication: ApiAuthenticationService,
    private i18n: I18nService,
    private inAppBrowser: InAppBrowserService,
    private nav: NavController,
    private pushNotif: PushNotificationService,
    private router: Router,
    private restaurantsRepo: RestaurantsRepository,
    private googleAnalyticsService: GoogleAnalyticsService,
    loggerService: LoggerService,
  ) {
    this.logger = loggerService.getLogger('AuthService');

    this.authRepository.userId$.subscribe((userId) => this.googleAnalyticsService.setUserId(userId));

    this.authRepository.currentSite$.subscribe((site) => {
      this.googleAnalyticsService.setUserSite(site);
      this.appInsightsService.setUserData(this.authRepository.getUser()?.id, site);
    });
  }

  loginWithPassword(request: LoginWithPasswordRequest): Observable<LoginWithPasswordResult> {
    return this.apiAuthentication.postLogin({ body: request }).pipe(
      this.authRepository.trackRequestStatus(AuthStatusKeys.Login),
      switchMap((result) => this.afterLogin(result.authToken, AuthStatusKeys.Login).pipe(map((_user) => result))),
      catchError((err: AppError) => {
        return this.handleLoginError(err, { email: request.login });
      }),
      tap((_: LoginWithPasswordResult) => this.redirectAfterLogin()),
    );
  }

  async loginWithJsonWebToken(request: LoginWithJsonWebTokenRequest, redirectUrl?: string): Promise<Observable<LoginWithJsonWebTokenResult>> {
    if (!request.token) {
      this.logger.error('Cannot login with Token -> toekn is empty');
      this.authRepository.loginFailed(AppErrorKeys.AnErrorOccured);
      return of();
    }

    if (this.authRepository.getIsLoggedIn()) {
      // signout
      this.logger.info('user already authenticated --> logout');
      await this.logout({ withRedirect: false });
    }

    return this.apiAuthentication.postLoginJwt({ body: request }).pipe(
      this.authRepository.trackRequestStatus(AuthStatusKeys.Login),
      switchMap((result) => {
        return this.afterLogin(result.authToken, AuthStatusKeys.Login).pipe(map((_user) => result));
      }),
      catchError((err: AppError) => {
        return this.handleLoginError(err, { email: '' }); // TODO : On ne connait pas l'email!!!
      }),
      tap((_: LoginWithSsoResult) => this.redirectAfterLogin(redirectUrl)),
    );
  }

  async loginWithSso(request: LoginWithSsoRequest, redirectUrl?: string): Promise<Observable<LoginWithSsoResult>> {
    if (!request.email || !request.timestamp || !request.token || !request.siteName) {
      this.logger.error('Cannot login with sso -> request/params are invalid', request);
      this.authRepository.loginFailed(AppErrorKeys.AnErrorOccured);
      return of();
    }

    if (this.authRepository.getIsLoggedIn()) {
      // If the user is already authenticated redirect the user to the right screen
      if (this.authRepository.getUser()?.email === request.email) {
        this.logger.info('user already authenticated --> redirect');
        this.redirectAfterLogin(redirectUrl);
        return of();
      }
      // the user authenticated is not the good one, signout
      else {
        this.logger.info('user already authenticated with another email --> logout');
        await this.logout({ withRedirect: false });
      }
    }

    return this.apiAuthentication.postLoginSso({ body: request }).pipe(
      this.authRepository.trackRequestStatus(AuthStatusKeys.Login),
      switchMap((result) => {
        return this.afterLogin(result.authToken, AuthStatusKeys.Login).pipe(map((_user) => result));
      }),
      catchError((err: AppError) => {
        return this.handleLoginError(err, { email: request.email });
      }),
      tap((_: LoginWithSsoResult) => this.redirectAfterLogin(redirectUrl)),
    );
  }

  async loginWithSaml(request: LoginWithSamlRequest): Promise<Observable<LoginWithSamlResult>> {
    if (this.authRepository.getIsLoggedIn()) {
      // signout
      this.logger.info('user already authenticated --> logout');
      await this.logout({ withRedirect: false });
    }

    return this.apiAuthentication.postLoginSaml({ body: request }).pipe(
      this.authRepository.trackRequestStatus(AuthStatusKeys.Login),
      tap(async (result: LoginWithSamlResult) => {
        // Si un seul provider est résolu, redirection vers l'URL SAML
        if (result.singleSignOnUrl) {
          await this.inAppBrowser.redirectWithCallback({
            url: result.singleSignOnUrl,
            openMode: 'system-browser',
            callbackPath: this.ssoCallbackPath,
            onCallbackPath: (path) => this.nav.navigateRoot(path),
          });
        }
        // Si plusieurs providers sont disponibles, le consommateur gère l'affichage
      }),
      catchError((err: AppError) => {
        return this.handleLoginError(err, { email: '' }); // TODO : On ne connait pas l'email!!!
      }),
    );
  }

  afterLogin(
    authToken: AuthTokenDto,
    statusKey: AuthStatusKeys.Login | AuthStatusKeys.LoadUser,
    cfg: { skipUpdateLanguage: boolean } = { skipUpdateLanguage: false },
  ): Observable<User> {
    this.authRepository.login(authToken);

    // silent register
    const onRegisterError = (e: any) => this.logger.warn('An error occured while trying to register device for Push Notifications', e);
    try {
      this.pushNotif.register().then(
        () => {
          // do nothing
        },
        (e) => onRegisterError(e),
      );
    } catch (e) {
      onRegisterError(e);
    }

    return this.loadUser(statusKey).pipe(
      tap((result) => {
        this.appInsightsService.setUserData(result.id, result.sites[0]);

        if (!cfg.skipUpdateLanguage) this.accountService.changeLanguage(result.language, { skipApiCall: true });
      }),
    );
  }

  redirectAfterLogin(forceRedirectUrl?: string | null) {
    // if no forceRedirectUrl given, we check if we have one in the authRepo
    const redirectUrl = forceRedirectUrl || this.authRepository.getRedirectUrlAfterNextLogin();

    if (redirectUrl && checkRedirectUrl(redirectUrl, this.logger)) {
      this.router.navigate([redirectUrl]);
    } else {
      this.router.navigate(['restaurants']);
    }

    // Clear redirect url in all case so we are sure we clean up
    this.authRepository.setRedirectUrlAfterNextLogin(null);
  }

  forgotPassword(email: string) {
    return this.apiAuthentication.postForgotPassword({ body: { email } }).pipe(
      this.authRepository.trackRequestStatus(AuthStatusKeys.ForgotPassword),
      tap(() => this.authRepository.forgotPassword()),
    );
  }

  resetPassword(resetPasswordRequest: ResetPasswordRequest) {
    return this.apiAuthentication.postResetPassword({ body: resetPasswordRequest }).pipe(
      this.authRepository.trackRequestStatus(AuthStatusKeys.ResetPassword),
      tap(() => this.authRepository.resetPassword()),
    );
  }

  changePassword(changePasswordRequest: ChangePasswordRequest) {
    return this.apiAuthentication.postChangePassword({ body: changePasswordRequest }).pipe(
      this.authRepository.trackRequestStatus(AuthStatusKeys.ChangePassword),
      tap((result: RefreshTokenResult) => this.authRepository.changePassword(result.authToken)),
    );
  }

  refreshAccountBalance() {
    if (!this.authRepository.getIsUserWithCashRegister()) return of();

    return this.apiPayments.getAccountBalance({ SiteName: this.authRepository.getCurrentSiteName() }).pipe(
      this.authRepository.accountBalanceDS.trackRequestStatus(),
      tap((result) => this.authRepository.refreshAccountBalance(result.accountBalance)),
    );
  }

  @ReusePendingObservable()
  refreshToken(): Observable<RefreshTokenResult> {
    const refreshToken = this.authRepository.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => appErrorFromKey(AppErrorKeys.NoRefreshToken));
    }

    this.logger.info('Refreshing AccessToken...');
    return this.apiAuthentication
      .postRefresh({
        body: {
          refreshToken,
        },
      })
      .pipe(
        switchMap((result: RefreshTokenResult) => {
          this.logger.info('AccessToken refreshed');
          return this.afterLogin(result.authToken, AuthStatusKeys.LoadUser, { skipUpdateLanguage: true }).pipe(map((_user) => result));
        }),
      );
  }

  /** Get a valid access token
   * @returns **null**, if no token
   * @returns **stored accessToken**, if access token IS NOT expired
   * @returns **new accessToken** after an refreshToken api call , if access token IS expired
   */
  getValidAccessToken(cfg: { allowRefresh: boolean }, source: string): Observable<string | null> {
    const token = this.authRepository.getAuthTokens();

    if (!token?.accessToken) return of(null);

    const expirationStatus = getDateExpirationStatusWithOffset(
      new Date(),
      ensureDate(token.accessTokenExpirationDate),
      environment.auth.refreshTokenBeforeExpirationMinutes * 60,
    );

    this.logger.debug('getValidAccessToken - expiration status is ' + expirationStatus);
    switch (expirationStatus) {
      case 'not-expired':
        // not expired, we just return the token
        return of(this.authRepository.getAccessToken()!);
      case 'expiring':
        // not expired but in offset, we return the token but also refresh the token silently for next time
        if (cfg.allowRefresh) this.refreshTokenSilently(source);
        return of(this.authRepository.getAccessToken()!);
      default:
        // expired, we refresh the token first from API and return it
        // but if allowRefresh is false, returns null
        if (!cfg.allowRefresh) return of(null);
        return this.refreshToken().pipe(map((x) => x.authToken.accessToken));
    }
  }

  tryRefreshTokenSilently(token: AuthTokenDto, source: string) {
    const tokenExpirationStatus = getDateExpirationStatusWithOffset(
      new Date(),
      ensureDate(token.accessTokenExpirationDate),
      environment.auth.refreshTokenBeforeExpirationMinutes * 60,
    );

    if (tokenExpirationStatus !== 'not-expired') {
      this.refreshTokenSilently(source);
    }
  }

  refreshTokenSilently(source: string) {
    this.logger.info('Refreshing token silently from ' + source);
    this.refreshToken()
      .pipe(
        catchError((err) => {
          this.logger.warn('Could not refresh token silently', { err });
          return of(false);
        }),
      )
      .subscribe();
  }

  loadUser(statusKey: AuthStatusKeys.Login | AuthStatusKeys.LoadUser) {
    return this.apiAuthentication.getUserInfo().pipe(
      // User Info Request status is currently linked to login
      this.authRepository.trackRequestStatus(statusKey),
      this.authRepository.accountBalanceDS.trackRequestStatus(),
      map((x) => map_UserInfoDto_User(x.userInfo)),
      tap((x) => {
        this.authRepository.setUser(x, statusKey);
        this.restaurantsRepo.setDefaultCurrentDishInformation(x!.sites[0]);
      }),
    );
  }

  updateAccountInfos(data: { lastName: string; firstName: string; phoneNumber: string }) {
    return this.apiAuthentication.patchAccountInfos({ body: { ...data } }).pipe(
      this.authRepository.trackRequestStatus(AuthStatusKeys.UpdateAccountInfos),
      map((result: UpdateAccountInfosResult) => {
        this.authRepository.updateAccountInfos(data, result.authToken);
        return true;
      }),
      catchError((_err) => {
        return of(false);
      }),
    );
  }

  updateAccountEmail(request: UpdateEmailRequest) {
    return this.apiAuthentication.patchAccountEmail({ body: request }).pipe(
      this.authRepository.trackRequestStatus(AuthStatusKeys.UpdateAccountEmail),
      tap((result: RefreshTokenResult) => this.authRepository.updateAccountEmail(request, result)),
    );
  }

  updateBalanceAlert(amount: number) {
    return this.apiAuthentication.postBalanceAlert({ body: { amount } }).pipe(
      this.authRepository.trackRequestStatus(AuthStatusKeys.UpdateBalanceAlert),
      map(() => {
        this.authRepository.updateBalanceAlertAmount(amount);
        return true;
      }),
      catchError((_err) => {
        return of(false);
      }),
    );
  }

  /** Clean user data in store and redirect to login */
  async logout(params: { withRedirect: boolean; withAlert?: boolean; redirectUrlAfterNextLogin?: string }) {
    this.logger.info('Logging out user', { params });
    if (params.withAlert) {
      const alert = await this.alertCtrl.create({
        header: this.i18n.translate('LOGOUT_ALERT.HEADER'),
        message: this.i18n.translate('LOGOUT_ALERT.MESSAGE'),
        buttons: [this.i18n.translate('LOGOUT_ALERT.NO'), { text: this.i18n.translate('LOGOUT_ALERT.YES'), role: 'confirm' }],
      });

      await alert.present();
      if ((await alert.onWillDismiss())?.role !== 'confirm') {
        return;
      }
    }

    const manuallyHandledRepo = ['account'];

    getRegistry().forEach((store) => {
      if (!manuallyHandledRepo.includes(store.name)) store.reset();
    });

    this.appInsightsService.clearUserData();
    this.accountRepo.reset();
    this.pushNotif.unregister();

    if (params.withRedirect) {
      if (params.redirectUrlAfterNextLogin) this.authRepository.setRedirectUrlAfterNextLogin(params.redirectUrlAfterNextLogin);

      this.router.navigate(['/login']);
    }
  }

  private handleLoginError(err: AppError, request: { email: string }) {
    if (err.errorKey === AppErrorKeys.ConfirmedEmailRequired) {
      this.authRepository.cancelLogin();
      this.router.navigate(['/confirm-email'], {
        queryParams: { callFrom: 'login', email: request.email },
      });
    }
    if (err.errorKey === AppErrorKeys.CompteClientNotFound) {
      this.nav.navigateRoot('/login', {
        queryParams: { errorKey: AppErrorKeys.CompteClientNotFound },
      });
    }
    return throwError(() => err);
  }

  checkEmailForSSO(email: string): Observable<CheckEmailForAuthenticationResult> {
    return this.apiAuthentication.postCheckEmail({ body: { email } }).pipe(
      this.authRepository.trackRequestStatus(AuthStatusKeys.CheckEmail),
      tap(() => this.authRepository.resetCheckEmailStatus()),
      catchError((err: AppError) => {
        this.logger.error('Error during checkEmailForSSO', { email, error: err });
        return throwError(() => err);
      }),
    );
  }
}
